<template>
  <div class="app-container">
    <!-- 搜索区域 -->
    <div class="filter-container">
      <div class="filter-row">
        <span class="filter-label">ID：</span>
        <el-input v-model="queryParams.uid" placeholder="请输入ID" style="width: 200px;" class="filter-item"
          @keyup.enter.native="handleQuery" />
      </div>
      <div class="filter-row">
        <span class="filter-label">用户名：</span>
        <el-input v-model="queryParams.nickname" placeholder="请输入用户名" style="width: 200px;" class="filter-item"
          @keyup.enter.native="handleQuery" />
      </div>
      <div class="filter-row">
        <span class="filter-label">来源：</span>
        <el-select v-model="queryParams.relation_type" placeholder="全部" clearable style="width: 140px;"
          class="filter-item">
          <el-option v-for="item in relationOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </div>
      <div class="filter-row">
        <span class="filter-label">所属子账号：</span>
        <el-input v-model="queryParams.sub_uid" placeholder="子账号UID" style="width: 160px;" class="filter-item"
          clearable @keyup.enter.native="handleQuery" />
      </div>
      <div class="filter-row">
        <span class="filter-label">注册时间：</span>
        <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
          end-placeholder="结束日期" class="filter-item" value-format="yyyy-MM-dd" @change="handleDateChange" />
      </div>
      <div class="filter-row button-row">
        <el-button class="filter-item" type="primary" @click="setToday">
          今日
        </el-button>
        <el-button class="filter-item" type="primary" @click="setYesterday">
          昨日
        </el-button>
        <el-button class="filter-item" type="success" @click="setLast7Days">
          近7天
        </el-button>
        <el-button class="filter-item" type="primary" icon="el-icon-search" @click="handleQuery">
          搜索
        </el-button>
        <el-button class="filter-item" type="default" icon="el-icon-refresh" @click="resetQuery">
          重置
        </el-button>
      </div>
    </div>

    <!-- 表格区域 -->
    <el-table :key="tableKey" v-loading="listLoading" :data="list" border fit highlight-current-row
      style="width: 100%;">
      <el-table-column label="序号" type="index" align="center" width="60" />
      <el-table-column label="ID" prop="uid" align="center" width="100" />
      <el-table-column label="昵称" prop="nick" align="center" />
      <el-table-column label="来源" align="center" width="110">
        <template slot-scope="{row}">
          <el-tag :type="getRelationTagType(row)" size="small">{{ getRelationLabel(row) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="归属子账号" align="center" width="120">
        <template slot-scope="{row}">
          <span>{{ getParentSubText(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="头像" align="center">
        <template slot-scope="{row}">
          <img v-if="row.avatar" :src="row.avatar" alt="头像" style="width: 50px; height: 50px; border-radius: 50%;">
          <span v-else>无头像</span>
        </template>
      </el-table-column>
      <el-table-column label="手机号" prop="mobile" align="center" width="130" />
      <el-table-column label="性别" align="center" width="70">
        <template slot-scope="{row}">
          <el-tag :type="row.gender === '女' ? 'danger' : ''" size="small">{{ row.gender }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="注册时间" align="center">
        <template slot-scope="{row}">
          <span>{{ row.create_time | parseTime('{y}-{m}-{d} {h}:{i}') }}</span>
        </template>
      </el-table-column>
      <el-table-column label="登录时间" align="center">
        <template slot-scope="{row}">
          <span>{{ row.last_login_time | parseTime('{y}-{m}-{d} {h}:{i}') }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="110">
        <template slot-scope="{row}">
          <!-- 仅本人添加的子账号可设置分成比例 -->
          <el-button v-if="row.is_sub_account === 1" type="text" size="mini" icon="el-icon-edit"
            @click="openRatioDialog(row)">设置分成</el-button>
          <span v-else>-</span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 设置分成比例弹窗 -->
    <el-dialog title="设置分成比例" :visible.sync="ratioDialogVisible" width="420px" :close-on-click-modal="false">
      <div v-if="ratioRow" class="ratio-dialog">
        <p>子账号：{{ ratioRow.nick || '-' }}（UID：{{ ratioRow.uid }}）</p>
        <el-input-number v-model="ratioValue" :min="0" :max="maxRatio" :precision="2" :step="1" />
        <span class="ratio-tip">%（0 ~ {{ maxRatio }}%，从您的收益中分给该子账号）</span>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="ratioDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="ratioSaving" @click="submitRatio">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 分页区域 -->
    <div class="pagination-container">
      <pagination v-show="total > 0" :total="total" :page.sync="queryParams.page" :limit.sync="queryParams.limit"
        @pagination="getList" />
    </div>
  </div>

</template>

<script>
import { parseTime } from '@/utils'
import { mapGetters } from 'vuex'
import Pagination from '@/components/Pagination'
import { getAction, setSubordinateRatio } from '@/api/user'
import {
  RELATION_OPTIONS,
  getRelationLabel,
  getRelationTagType,
  getParentSubText,
  withRelationType,
  isSuccess
} from '@/utils/agentRelation'

export default {
  name: 'InvitationList',
  components: { Pagination },
  filters: {
    parseTime
  },
  data() {
    return {
      tableKey: 0,
      list: null,
      total: 0,
      listLoading: true,
      dateRange: null,
      relationOptions: RELATION_OPTIONS,
      queryParams: {
        page: 1,
        limit: 10,
        role: 0,
        uid: '',
        nickname: '',
        relation_type: '',
        sub_uid: '',
        startTime: '',
        endTime: ''
      },
      // 设置分成比例
      ratioDialogVisible: false,
      ratioSaving: false,
      ratioRow: null,
      ratioValue: 0
    }
  },
  computed: {
    ...mapGetters([
      'agentMaxRatio'
    ]),
    maxRatio() {
      return this.agentMaxRatio
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getRelationLabel,
    getRelationTagType,
    getParentSubText,

    // 设置分成比例
    openRatioDialog(row) {
      this.ratioRow = row
      this.ratioValue = Number(row.ratio) || 0
      this.ratioDialogVisible = true
    },

    async submitRatio() {
      if (!this.ratioRow) return
      if (this.ratioValue < 0 || this.ratioValue > this.maxRatio) {
        this.$message.error(`分成比例上限为 ${this.maxRatio}%`)
        return
      }
      this.ratioSaving = true
      try {
        const res = await setSubordinateRatio({
          subordinate_id: this.ratioRow.id,
          ratio: this.ratioValue
        })
        if (isSuccess(res)) {
          this.$message.success('分成比例设置成功')
          this.ratioDialogVisible = false
          this.getList()
        } else {
          this.$message.error(res.msg || res.message || '设置失败')
        }
      } catch (error) {
        this.$message.error(error.message || '设置失败')
      } finally {
        this.ratioSaving = false
      }
    },

    async getList() {
      this.listLoading = true
      try {
        // 构建请求参数
        const params = {
          page: this.queryParams.page,
          limit: this.queryParams.limit
        }

        // 添加搜索条件
        if (this.queryParams.uid) {
          params.uid = this.queryParams.uid
        }
        if (this.queryParams.nickname) {
          params.nickname = this.queryParams.nickname
        }
        // 来源筛选：不传 = 全部（与改造前一致）
        withRelationType(params, this.queryParams.relation_type)
        // 只看某个子账号邀请的人
        if (this.queryParams.sub_uid) {
          params.sub_uid = this.queryParams.sub_uid.trim()
        }
        if (this.queryParams.startTime) {
          params.startTime = this.queryParams.startTime + ' 00:00:00'
        }
        if (this.queryParams.endTime) {
          params.endTime = this.queryParams.endTime + ' 23:59:59'
        }
         params.role = this.queryParams.role
        const res = await getAction('/api/center/inviteList', params)

        if (isSuccess(res)) {
          this.list = res.data.data || []
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.msg || '获取数据失败')
          this.list = []
          this.total = 0
        }
      } catch (error) {
        console.error('获取邀请列表失败:', error)
        this.$message.error('获取数据失败，请稍后重试')
        this.list = []
        this.total = 0
      } finally {
        this.listLoading = false
      }
    },

    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    handleQuery() {
      this.queryParams.page = 1
      this.getList()
    },
    handleDateChange(dates) {
      if (dates && dates.length === 2) {
        this.queryParams.startTime = dates[0]
        this.queryParams.endTime = dates[1]
      } else {
        this.queryParams.startTime = ''
        this.queryParams.endTime = ''
      }
    },

    setToday() {
      const today = new Date()
      const todayStr = this.formatDate(today)

      this.dateRange = [todayStr, todayStr]
      this.queryParams.startTime = todayStr
      this.queryParams.endTime = todayStr

      // 直接查询
      this.handleQuery()
    },

    setYesterday() {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const yesterdayStr = this.formatDate(yesterday)

      this.dateRange = [yesterdayStr, yesterdayStr]
      this.queryParams.startTime = yesterdayStr
      this.queryParams.endTime = yesterdayStr

      // 直接查询
      this.handleQuery()
    },

    setLast7Days() {
      const end = new Date()
      const start = new Date()
      start.setDate(end.getDate() - 6) // 近7天包括今天

      this.dateRange = [
        this.formatDate(start),
        this.formatDate(end)
      ]

      this.queryParams.startTime = this.formatDate(start)
      this.queryParams.endTime = this.formatDate(end)

      // 直接查询
      this.handleQuery()
    },



    resetQuery() {
      this.dateRange = null
      this.queryParams = {
        page: 1,
        limit: 10,
        role: 0,
        uid: '',
        nickname: '',
        relation_type: '',
        sub_uid: '',
        startTime: '',
        endTime: ''
      }
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  padding-bottom: 10px;

  .filter-row {
    display: inline-block;
    margin-right: 20px;
    margin-bottom: 10px;
    vertical-align: top;

    .filter-label {
      margin-right: 8px;
      min-width: 60px;
      white-space: nowrap;
    }

    .filter-item {
      display: inline-block;
      vertical-align: middle;
      margin-right: 10px;
    }
  }

  .button-row {
    display: inline-block;
    margin-right: 0;

    .filter-item {
      margin-right: 10px;
    }
  }
}

.status-col {
  .el-tag {
    margin-right: 8px;
  }
}

.ratio-dialog {
  font-size: 14px;
  color: #606266;

  p {
    margin: 0 0 15px;
  }
}

.ratio-tip {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}

// 移动端适配
@media (max-width: 768px) {
  .filter-container {
    font-size: 12px;

    .filter-row {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      margin-bottom: 8px;
      white-space: nowrap;
      margin-right: 0;
      align-items: center;

      .filter-label {
        min-width: 50px;
        font-size: 12px;
        display: flex;
        align-items: center;
        height: 32px;
      }

      .filter-item {
        flex: 1;
        display: flex;
        align-items: center;
      }
    }

    .el-input,
    .el-date-editor {
      width: 100% !important;
      min-width: 120px !important;
    }

    .button-row {
      display: flex;
      flex-wrap: wrap;

      .filter-item {
        flex: none;
        margin-right: 5px;
        margin-bottom: 5px;

        .el-button {
          padding: 4px 6px !important;
          font-size: 11px !important;
        }
      }
    }
  }

  // 表格适配
  .el-table {
    font-size: 12px;

    .el-table__cell {
      padding: 5px 2px;
    }
  }

  // 分页适配
  .pagination-container {
    text-align: center;

    .el-pagination {
      justify-content: center;

      .el-pagination__sizes,
      .el-pagination__jump {
        display: none;
      }
    }
  }
}

@media (max-width: 480px) {
  .filter-container {
    padding: 5px;
    font-size: 11px;

    .filter-item {
      padding: 8px 10px !important;
      font-size: 12px;
      white-space: nowrap;
    }

    .filter-row {
      display: flex;
      flex-direction: row;
      margin-bottom: 5px;
      white-space: nowrap;
      margin-right: 0;
      align-items: center;

      .filter-label {
        min-width: 40px;
        font-size: 10px;
        display: flex;
        align-items: center;
        height: 28px;
      }

      .filter-item {
        flex: 1;
        display: flex;
        align-items: center;
      }
    }

    .el-input,
    .el-date-editor {
      width: 100% !important;
      min-width: 80px !important;
      font-size: 10px !important;
    }

    .button-row {
      display: flex;
      flex-wrap: wrap;

      .filter-item {
        flex: none;
        margin-right: 3px;
        margin-bottom: 3px;

        .el-button {
          padding: 3px 5px !important;
          font-size: 10px !important;
        }
      }
    }
  }

  .el-table {
    font-size: 11px;

    .el-table__cell {
      padding: 3px 1px;
    }

    // 隐藏头像列在小屏幕上
    .el-table__header-wrapper,
    .el-table__body-wrapper {
      .el-table__row {

        td:nth-child(3),
        th:nth-child(3) {
          display: none;
        }
      }
    }
  }
}
</style>